// The sea mine and the deployable cards (v2-the-deep.md §11.2, §11.3,
// §11.9, §15.6; mockup round 088 card 13; R2654, R2658, R2902).
//
// A sea object is a map object with an owner, so it joins the card family
// as a fact does: the owner ink on the name, the mark at the left, one
// chip. Two grades reach the client (`State.objects`):
//
// - A KNOWN row carries the kind, the arm and the hull (R2894). Its card
//   names the kind, the family and whose it is, the chip ARMED, ARMING or
//   INERT (a sea mine, R2737) and ANCHORED or ANCHORING (a deployable), the
//   hull as a vital, the life left of an own sea mine as a fact, and the
//   one rule a reader must not miss as the note.
// - A HEARD row is a sea mine that a hull at sea alone hears (R2902): it
//   carries the id, the faction and the point alone. Its card names an
//   unknown sea mine: "heard, kind unknown", the faction and the hatched
//   hull.
//
// The card opens from the water peek of the cell (`fast/tooltip.ts`).
// Several sea objects under one finger (§15.6, R2635): a press picks the
// nearest item of the cell and peeks its card; a long press, or a second
// click on a desktop, lists every object of the cell in the cell list of
// `ui/cards/cellpeek.ts` (R2851).
//
// The module reads `SeaObjectView`, the fields of a row that every store
// of `State.objects` keeps, with the point in world px: the store of the
// canvas lane (`SeaObjectRow`) fits it as it stands, and
// `viewOfWire` reads a wire row. Everything here is pure.

import { SeaObjectKind } from '../../gen/proto/aquaphobia';
import type { SeaObject } from '../../gen/proto/aquaphobia';
import { card as S } from '../../gen/strings';
import { type GameState, dur } from '../../state';
import { cellAt, factionName, mpxToPx } from '../../world';
import { esc } from '../esc';
import { cssColor, pctFig } from '../format';
import type { CardSize, CardSpec, Fact, Vital } from './card';
import { markSvg } from './marks';

/** What the cards read of the state. */
export type SeaObjectState = Pick<GameState, 'seaObject' | 'faction'>;

/** One sea object as the cards read it: a row of `State.objects` with
 *  its point in world px. `leftSec` is the life left of an own sea mine,
 *  as the store counts it down: map seconds (R2697), or world seconds
 *  for a mine of a pre-R2697 snapshot. */
export interface SeaObjectView {
  id: number;
  faction: number;
  kind: SeaObjectKind;
  x: number;
  y: number;
  armed: boolean;
  hullPct: number;
  own: boolean;
  /** A sea mine that a hull at sea alone hears: no kind, no arm (R2902). */
  heard: boolean;
  /** A sea mine in harbour limits: unarmed for good (§11.2 rule 5). */
  inert: boolean;
  leftSec: number;
}

/** A wire row as the cards read it. */
export function viewOfWire(o: SeaObject): SeaObjectView {
  return {
    id: o.id, faction: o.faction, kind: o.kind, x: mpxToPx(o.xMpx), y: mpxToPx(o.yMpx),
    armed: o.armed, hullPct: o.hullPct, own: o.own, heard: o.heard, inert: o.inert, leftSec: o.leftMapSec || o.leftSec,
  };
}

/**
 * The rows that stand in `cell`, the nearest to the world point first
 * (§15.6): a press picks the first. Two rows at one distance keep the id
 * order, so the pick never flickers between frames.
 */
export function seaObjectsIn<T extends SeaObjectView>(rows: readonly T[], cell: number, x: number, y: number): T[] {
  if (cell < 0) return [];
  const out: { o: T; d: number }[] = [];
  for (const o of rows) {
    if (cellAt(o.x, o.y) !== cell) continue;
    out.push({ o, d: Math.hypot(o.x - x, o.y - y) });
  }
  out.sort((a, b) => a.d - b.d || a.o.id - b.o.id);
  return out.map((e) => e.o);
}

/** Is the kind a sea mine? The catalog says it; a kind the catalog does
 *  not list reads by its enum value, the four sea mines first. */
function isSeaMine(s: SeaObjectState, kind: SeaObjectKind): boolean {
  const spec = s.seaObject(kind);
  return spec ? spec.seaMine : kind >= SeaObjectKind.SEA_OBJECT_KIND_BARB && kind <= SeaObjectKind.SEA_OBJECT_KIND_PETARD;
}

/** Whose a known row is, as the kind line says it. */
function whoseKind(s: SeaObjectState, o: SeaObjectView, family: string): string {
  if (o.own) return S.so_kind_own({ family });
  if (o.faction === s.faction) return S.so_kind_mate({ family });
  return S.so_kind_hostile({ family });
}

/** The chip of a known row: the arm of a sea mine, the anchor of a
 *  deployable. A sea mine in harbour limits never arms: INERT (R2737). */
function armChip(o: SeaObjectView, mine: boolean): { text: string; tone: 'bad' | 'amber' | '' } {
  if (mine && o.inert) return { text: S.so_inert, tone: '' };
  if (mine) return o.armed ? { text: S.so_armed, tone: 'bad' } : { text: S.so_arming, tone: 'amber' };
  return o.armed ? { text: S.so_anchored, tone: 'bad' } : { text: S.so_anchoring, tone: 'amber' };
}

/** The hull of a known row, as the wire states it in percent. */
function hullVital(o: SeaObjectView): Vital {
  return { label: S.v_hull, fig: pctFig(o.hullPct), frac: o.hullPct / 100, ink: 'hull' };
}

/** The body a page adds to the card of a known sea object, or none: the
 *  listening watch of chart card 47 on an own anchored picket buoy
 *  (R2657). The chart piece sets it; an answer of '' adds no body. */
let objectBody: ((o: SeaObjectView) => string) | null = null;

/** Sets the body of `seaObjectCard` (the listening watch, R2657). */
export function setSeaObjectBody(fn: ((o: SeaObjectView) => string) | null): void { objectBody = fn; }

/** The card of a heard row (R2902): an unknown sea mine, where and whose alone. */
function heardCard(o: SeaObjectView, size: CardSize): CardSpec {
  return {
    size, mark: markSvg('seamine', cssColor(o.faction)), title: S.so_heard_title, ink: { faction: o.faction },
    kind: S.so_heard_kind, status: { text: S.st_heard }, close: size !== 'peek',
    vitals: [{ label: S.v_hull, fig: '', frac: 0, unknown: true }],
    facts: [{ key: S.f_faction, val: esc(factionName(o.faction)) }],
    note: { lines: [esc(S.so_heard_note)] },
  };
}

/** The card of one sea object (round 088 card 13). */
export function seaObjectCard(s: SeaObjectState, o: SeaObjectView, size: CardSize = 'peek'): CardSpec {
  if (o.heard) return heardCard(o, size);
  const mine = isSeaMine(s, o.kind);
  const family = mine ? S.so_mine : S.so_deployable;
  const facts: Fact[] = [];
  if (!o.own) facts.push({ key: S.f_faction, val: esc(factionName(o.faction)) });
  if (o.own && mine && o.leftSec > 0) facts.push({ key: S.f_life, val: esc(dur(o.leftSec)) });
  const hostile = !o.own && o.faction !== s.faction;
  const note = mine ? (hostile ? S.so_note_mine_hostile : S.so_note_mine)
    : hostile ? S.so_note_hostile_deployable : S.so_note_own_deployable;
  return {
    size, mark: markSvg(mine ? 'seamine' : 'deployable', cssColor(o.faction)),
    title: s.seaObject(o.kind)?.name ?? family, ink: { faction: o.faction },
    kind: whoseKind(s, o, family), status: armChip(o, mine), close: size !== 'peek',
    vitals: [hullVital(o)], facts,
    note: { lines: [esc(note)] },
    ...bodyOf(o),
  };
}

/** The body of `objectBody` as the card and the peek draw it. */
function bodyOf(o: SeaObjectView): { body?: string; peekBody?: string } {
  const html = objectBody?.(o) ?? '';
  return html ? { body: html, peekBody: html } : {};
}
